import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowLeft, Calendar, Clock, User, ArrowRight, Share2, 
  BookOpen, GraduationCap, FileText, Users, BarChart, 
  DollarSign, Target, Brain, Shield, LineChart, Scale, 
  Landmark, Calculator, Briefcase, Globe, Percent, Award,
  Building, TrendingUp, PiggyBank, Coins, CreditCard, Wallet, BadgeDollarSign,
  CheckCircle, Lock, ExternalLink
} from 'lucide-react';
import blogPostsData from '../data/blogPosts';
import additionalBlogPosts from '../data/additionalBlogPosts';

// Combine all blog posts
const allBlogPosts = [...blogPostsData, ...additionalBlogPosts];

// Icon mapping
const iconMap = {
  GraduationCap, FileText, Users, BarChart, DollarSign, Target, Brain, Shield,
  LineChart, Scale, Landmark, Calculator, Briefcase, Globe, Percent, Award,
  Building, TrendingUp, PiggyBank, Coins, CreditCard, Wallet, BadgeDollarSign, BookOpen
};

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  // Find the blog post
  const post = allBlogPosts.find(p => p.slug === slug);
  
  // If post not found, show 404-like message
  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="blog-post-not-found">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Article Not Found</h1>
          <p className="text-slate-400 mb-8">The article you're looking for doesn't exist.</p>
          <Link 
            to="/blog" 
            className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  // Get related posts (same category, excluding current)
  const relatedPosts = allBlogPosts
    .filter(p => p.category === post.category && p.slug !== slug)
    .slice(0, 3);

  // Get icon component
  const IconComponent = iconMap[post.icon] || BookOpen;

  // Structured data for the blog post
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "datePublished": post.date,
    "dateModified": post.date,
    "author": {
      "@type": "Person",
      "name": "Harry Vadalkar",
      "url": "https://harryvadalkar.ca"
    },
    "publisher": {
      "@type": "Person",
      "name": "Harry Vadalkar"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://harryvadalkar.ca/blog/${post.slug}`
    },
    "keywords": post.keywords
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div className="min-h-screen" data-testid="blog-post-page">
      <Helmet>
        <title>{post.title} | Harry Vadalkar Blog</title>
        <meta name="description" content={post.excerpt} />
        <meta name="keywords" content={post.keywords} />
        <link rel="canonical" href={`https://harryvadalkar.ca/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:url" content={`https://harryvadalkar.ca/blog/${post.slug}`} />
        <meta property="article:author" content="Harry Vadalkar" />
        <meta property="article:published_time" content={post.date} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 to-[#020617]" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back button */}
          <button
            onClick={() => navigate('/blog')}
            className="inline-flex items-center text-slate-400 hover:text-white mb-8 transition-colors"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </button>

          {/* Category badge */}
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <IconComponent className="w-5 h-5 text-blue-400" />
            </div>
            <span className="text-blue-400 font-medium">{post.category}</span>
          </div>

          {/* Title */}
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            {post.title}
          </h1>

          {/* Meta info */}
          <div className="flex flex-wrap items-center gap-6 text-slate-400 mb-8">
            <span className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {post.author}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {post.date}
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {post.readTime}
            </span>
            <button 
              onClick={handleShare}
              className="flex items-center gap-2 hover:text-blue-400 transition-colors"
              data-testid="share-button"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
          </div>

          {/* Excerpt */}
          <p className="text-xl text-slate-300 leading-relaxed">
            {post.excerpt}
          </p>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-12" data-testid="blog-post-content">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-invert prose-lg max-w-none">
            {/* Render markdown-like content */}
            <div 
              className="blog-content"
              dangerouslySetInnerHTML={{ 
                __html: post.content
                  // Headers
                  .replace(/^## (.*$)/gim, '<h2>$1</h2>')
                  .replace(/^### (.*$)/gim, '<h3>$1</h3>')
                  // Bold text - handle inline bold
                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                  // List items
                  .replace(/^- (.*$)/gim, '<li>$1</li>')
                  .replace(/^\* (.*$)/gim, '<li>$1</li>')
                  // Numbered lists
                  .replace(/^\d+\. (.*$)/gim, '<li>$1</li>')
                  // Paragraphs - convert double newlines
                  .replace(/\n\n/g, '</p><p>')
                  // Single newlines within paragraphs
                  .replace(/\n(?!<)/g, '<br/>')
                  // Wrap in paragraph tags
                  .replace(/^(?!<)/, '<p>')
                  .replace(/(?!>)$/, '</p>')
                  // Clean up empty paragraphs
                  .replace(/<p><\/p>/g, '')
                  .replace(/<p><br\/><\/p>/g, '')
              }}
            />
          </article>

          {/* CTA Section */}
          <div className="mt-12 p-8 glass rounded-xl border border-blue-500/30" data-testid="article-cta">
            <h3 className="text-2xl font-bold text-white mb-4">Ready to Take Action?</h3>
            <p className="text-slate-300 mb-6">
              Don't just read about it—start your journey to mastering Canadian finance today.
            </p>
            <a
              href={post.cta.link}
              target={post.cta.link.startsWith('http') ? '_blank' : '_self'}
              rel={post.cta.link.startsWith('http') ? 'noopener noreferrer' : ''}
              className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-4 rounded-full transition-all"
            >
              {post.cta.text}
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </div>

          {/* IFC Course Promo - Show for IFC related posts */}
          {post.category === 'IFC Exam Prep' && (
            <div className="mt-8 bg-gradient-to-r from-emerald-900/30 to-slate-800/50 border border-emerald-500/30 rounded-xl p-6" data-testid="ifc-course-promo">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-emerald-400" />
                IFC Exam Preparation Course
              </h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                {/* Lifetime Access */}
                <div className="bg-slate-800/60 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="bg-emerald-500 text-white text-xs font-bold px-2 py-0.5 rounded">BEST VALUE</span>
                    <span className="text-emerald-400 font-bold">CAD $99.99</span>
                  </div>
                  <h4 className="text-white font-semibold mb-2">Lifetime Access</h4>
                  <ul className="text-xs text-slate-400 space-y-1 mb-3">
                    <li className="flex items-start gap-1.5">
                      <CheckCircle className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span>18 chapter video lectures and podcasts</span>
                    </li>
                    <li className="flex items-start gap-1.5">
                      <CheckCircle className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span>30 practice questions per chapter</span>
                    </li>
                    <li className="flex items-start gap-1.5">
                      <CheckCircle className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span>6 mock exams with worked solutions</span>
                    </li>
                    <li className="flex items-start gap-1.5">
                      <CheckCircle className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span>Flashcards, infographics & scenario games</span>
                    </li>
                  </ul>
                  <a
                    href="https://www.paypal.com/ncp/payment/TP4VL7H55HWSL"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold py-2 rounded text-center transition-colors"
                  >
                    Buy Lifetime Access
                  </a>
                  <p className="text-xs text-slate-500 mt-2 text-center">Access code delivered by email within 24 hours</p>
                </div>

                {/* YouTube Membership */}
                <div className="bg-slate-800/60 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="bg-red-600 text-white text-xs font-bold px-2 py-0.5 rounded">YOUTUBE</span>
                    <span className="text-white font-bold">CAD $34.99<span className="text-xs font-normal text-slate-400">/mo</span></span>
                  </div>
                  <h4 className="text-white font-semibold mb-2">Channel Membership</h4>
                  <ul className="text-xs text-slate-400 space-y-1 mb-3">
                    <li className="flex items-start gap-1.5">
                      <CheckCircle className="w-3 h-3 text-slate-500 mt-0.5 flex-shrink-0" />
                      <span>Full access to all IFC course content</span>
                    </li>
                    <li className="flex items-start gap-1.5">
                      <CheckCircle className="w-3 h-3 text-slate-500 mt-0.5 flex-shrink-0" />
                      <span>Members-only videos and live streams</span>
                    </li>
                    <li className="flex items-start gap-1.5">
                      <CheckCircle className="w-3 h-3 text-slate-500 mt-0.5 flex-shrink-0" />
                      <span>Community access and Q&A sessions</span>
                    </li>
                    <li className="flex items-start gap-1.5">
                      <CheckCircle className="w-3 h-3 text-slate-500 mt-0.5 flex-shrink-0" />
                      <span>Access to CSC and CIRO content as well</span>
                    </li>
                  </ul>
                  <a
                    href="https://www.youtube.com/@FinanceinCanada/join"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full bg-red-600 hover:bg-red-500 text-white text-sm font-semibold py-2 rounded text-center transition-colors"
                  >
                    Join YouTube Membership
                  </a>
                  <p className="text-xs text-slate-500 mt-2 text-center">Flexible monthly subscription</p>
                </div>
              </div>

              <div className="mt-4 text-center">
                <Link
                  to="/ifc"
                  className="inline-flex items-center text-emerald-400 hover:text-emerald-300 text-sm font-medium"
                >
                  View Full Course Details
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Author Section */}
      <section className="py-12 bg-slate-900/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center gap-6 p-6 glass rounded-xl">
            <img
              src="https://customer-assets.emergentagent.com/job_e36899ae-c2fa-440f-bf36-c9469e111964/artifacts/f8pxlvyc_HarryVadalkar-Website-4.jpg"
              alt="Harry Vadalkar"
              className="w-24 h-24 rounded-full object-cover"
            />
            <div>
              <h3 className="text-xl font-bold text-white mb-2">About Harry Vadalkar</h3>
              <p className="text-slate-300 mb-3">
                Harry is a Harvard & MIT certified financial educator, CFA charterholder, and author of the bestselling book "Wealthy Canadian." 
                He has trained over 30,000 students in Canadian investing and exam preparation.
              </p>
              <div className="flex gap-4">
                <Link to="/youtube" className="text-blue-400 hover:text-blue-300 text-sm">
                  YouTube Channel
                </Link>
                <Link to="/courses" className="text-blue-400 hover:text-blue-300 text-sm">
                  View Courses
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16" data-testid="related-posts">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost, index) => {
                const RelatedIcon = iconMap[relatedPost.icon] || BookOpen;
                return (
                  <Link
                    key={index}
                    to={`/blog/${relatedPost.slug}`}
                    className="glass rounded-xl p-6 hover:border-blue-500/30 transition-all group"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 rounded-lg bg-blue-500/20">
                        <RelatedIcon className="w-4 h-4 text-blue-400" />
                      </div>
                      <span className="text-blue-400 text-sm">{relatedPost.category}</span>
                    </div>
                    <h3 className="font-semibold text-white group-hover:text-blue-400 transition-colors line-clamp-2 mb-2">
                      {relatedPost.title}
                    </h3>
                    <p className="text-slate-400 text-sm line-clamp-2">{relatedPost.excerpt}</p>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-900/30 to-slate-900/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Continue Your Learning Journey
          </h2>
          <p className="text-slate-300 mb-8">
            Explore more articles or jump into our comprehensive courses.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/blog"
              className="inline-flex items-center bg-slate-700 hover:bg-slate-600 text-white font-semibold px-6 py-3 rounded-full transition-all"
            >
              <BookOpen className="w-5 h-5 mr-2" />
              More Articles
            </Link>
            <Link
              to="/courses"
              className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-full transition-all"
            >
              View All Courses
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
